import React, { useState, useEffect } from "react";
import { LazyLoadImage } from "react-lazy-load-image-component";
import CustomModal from "./customModal";
import useDarkMode from '../../../hooks/useDarkMode';

export default function CategoryModal({
  showParentCategoryModal,
  setShowParentCategoryModal,
  showButton,
  onSubmit,
  setSelectedParentCategory,
  selectedParentCategory,
  categories,
  isMultiSelect = false,
  modalTitle,
  modalSubTitle,
  fetchCategories,
  excludeAddOnCategories = false,
  isLoadingCategories = false,
}) {
  const [searchTerm, setSearchTerm] = useState("");
  const [filteredCategories, setFilteredCategories] = useState(categories);
  const [expandedCategories, setExpandedCategories] = useState(new Set());
  const [tempSelectedCategories, setTempSelectedCategories] = useState(
    selectedParentCategory || (isMultiSelect ? [] : null)
  );
  const isDarkMode = useDarkMode();

  useEffect(() => {
    if (showParentCategoryModal && typeof fetchCategories === "function") {
      fetchCategories(); // Refetch categories when modal opens
    }
  }, [showParentCategoryModal, fetchCategories]);

  // Filter function to exclude add-on categories
  const filterAddOnCategories = (categoriesArray) => {
    if (excludeAddOnCategories === true) {
      // When excludeAddOnCategories is true, remove categories with IsAddOnCategory === true
      return categoriesArray
        .map((category) => {
          if (category && category.isAddOnCategory === true) {
            return null;
          }
          
          // Recursively filter subcategories
          let filteredSubCategories = [];
          if (
            category &&
            category.subCategoriesList &&
            category.subCategoriesList.length > 0
          ) {
            filteredSubCategories = filterAddOnCategories(category.subCategoriesList);
          }
          
          if (!category) return null;
          
          return {
            ...category,
            subCategoriesList: filteredSubCategories,
          };
        })
        .filter(Boolean);
    }
    // When excludeAddOnCategories is false, return all categories as-is
    return categoriesArray;
  };

  useEffect(() => {
    // First filter out add-on categories if needed
    const categoriesWithoutAddOn = excludeAddOnCategories === true 
      ? filterAddOnCategories(categories)
      : categories;
    
    if (!searchTerm.trim()) {
      setFilteredCategories(categoriesWithoutAddOn);
    } else {
      const filtered = filterCategories(
        categoriesWithoutAddOn,
        searchTerm.trim().toLowerCase()
      );
      setFilteredCategories(filtered);
    }
  }, [searchTerm, categories, excludeAddOnCategories]);

  useEffect(() => {
    setTempSelectedCategories(selectedParentCategory || (isMultiSelect ? [] : null));
  }, [selectedParentCategory, isMultiSelect]);

  const handleCategoryClick = () => {
    if (typeof onSubmit === "function") {
      onSubmit(tempSelectedCategories);
      setSelectedParentCategory(tempSelectedCategories);
    }
    setShowParentCategoryModal(false);
  };

  const filterCategories = (categoriesArray, term) => {
    if (!term) return categoriesArray;
    return categoriesArray
      .map((category) => {
        const matchesSearch = category.rootCat_Name
          .trim()
          .toLowerCase()
          .includes(term);

        let filteredSubCategories = [];
        if (
          category.subCategoriesList &&
          category.subCategoriesList.length > 0
        ) {
          filteredSubCategories = filterCategories(
            category.subCategoriesList,
            term
          );
        }

        if (matchesSearch || filteredSubCategories.length > 0) {
          return {
            ...category,
            subCategoriesList: filteredSubCategories,
          };
        }

        return null;
      })
      .filter(Boolean);
  };

  const toggleExpand = (categoryId) => {
    setExpandedCategories((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(categoryId)) {
        newSet.delete(categoryId);
      } else {
        newSet.add(categoryId);
      }
      return newSet;
    });
  };

  const handleCategorySelection = (category) => {
    if (isMultiSelect) {
      const currentSelection = Array.isArray(tempSelectedCategories)
        ? tempSelectedCategories.filter(cat => cat !== null && cat !== undefined)
        : [];
      const isSelected = currentSelection.some(
        (cat) => cat && cat.rootCat_ID === category.rootCat_ID
      );
      if (isSelected) {
        setTempSelectedCategories(
          currentSelection.filter(
            (cat) => cat && cat.rootCat_ID !== category.rootCat_ID
          )
        );
      } else {
        setTempSelectedCategories([...currentSelection, category]);
      }
    } else {
      setTempSelectedCategories(category);
    }
  };

  const RenderCategoryRow = ({
    category,
    index,
    level = 0,
    parentIndex = null,
    grandParentIndex = null,
  }) => {
    // Safety check: return null if category is null or undefined
    if (!category || !category.rootCat_ID) {
      return null;
    }

    const isExpanded = expandedCategories.has(category.rootCat_ID);
    const hasSubcategories =
      category.subCategoriesList && category.subCategoriesList.length > 0;
    const isSelected = isMultiSelect
      ? Array.isArray(tempSelectedCategories) && tempSelectedCategories.some(
          (cat) => cat && cat.rootCat_ID === category.rootCat_ID
        )
      : tempSelectedCategories?.rootCat_ID === category.rootCat_ID;

    return (
      <>
        <div
          className="category-item-card"
          style={{
            padding: "12px 16px",
            marginBottom: "8px",
            background: isDarkMode ? (isSelected ? '#2d4a5f' : '#404040') : (isSelected ? '#e7f3ff' : '#ffffff'),
            borderRadius: "8px",
            border: isSelected 
              ? (isDarkMode ? '2px solid #4a90e2' : '2px solid #007bff')
              : (isDarkMode ? '1px solid #555' : '1px solid #e9ecef'),
            cursor: "pointer",
            transition: "all 0.2s ease",
            marginLeft: level > 0 ? `${level * 24}px` : '0',
          }}
          onClick={() => handleCategorySelection(category)}
          onMouseEnter={(e) => {
            if (!isSelected) {
              e.currentTarget.style.background = isDarkMode ? '#4a4a4a' : '#f8f9fa';
              e.currentTarget.style.borderColor = isDarkMode ? '#666' : '#dee2e6';
            }
          }}
          onMouseLeave={(e) => {
            if (!isSelected) {
              e.currentTarget.style.background = isDarkMode ? '#404040' : '#ffffff';
              e.currentTarget.style.borderColor = isDarkMode ? '#555' : '#e9ecef';
            }
          }}
        >
          <div className="d-flex align-items-center justify-content-between">
            <div className="d-flex align-items-center flex-grow-1">
              {hasSubcategories && (
                <div
                  className="me-3"
                  style={{ cursor: "pointer", width: "20px", color: isDarkMode ? '#ffffff' : '#6c757d' }}
                  onClick={(e) => {
                    e.stopPropagation();
                    toggleExpand(category.rootCat_ID);
                  }}
                >
                  <i
                    className={`fa ${
                      isExpanded ? "fa-chevron-down" : "fa-chevron-right"
                    }`}
                    style={{ fontSize: '12px' }}
                  ></i>
                </div>
              )}

              {!hasSubcategories && (
                <div className="me-3" style={{ width: "20px" }}></div>
              )}

              <span 
                style={{ 
                  fontSize: '15px',
                  fontWeight: isSelected ? '500' : '400',
                  color: isDarkMode ? '#ffffff' : '#495057'
                }}
              >
                {category.rootCat_Name}
              </span>
            </div>

            <div className="form-check ms-3">
              <input
                type={isMultiSelect ? "checkbox" : "radio"}
                className="form-check-input"
                checked={isSelected}
                onChange={() => handleCategorySelection(category)}
                name={
                  isMultiSelect
                    ? `checkbox-${category.rootCat_ID}`
                    : "category-selection"
                }
                id={`category-${
                  isMultiSelect ? "checkbox" : "radio"
                }-${category.rootCat_ID}`}
                onClick={(e) => e.stopPropagation()}
                style={{
                  width: '18px',
                  height: '18px',
                  cursor: 'pointer'
                }}
              />
            </div>
          </div>
        </div>

        {hasSubcategories &&
          isExpanded &&
          category.subCategoriesList
            .filter(subcategory => subcategory !== null && subcategory !== undefined)
            .map((subcategory, subIndex) => (
              <RenderCategoryRow
                key={subcategory.rootCat_ID}
                category={subcategory}
                index={subIndex}
                level={level + 1}
                parentIndex={index}
                grandParentIndex={parentIndex}
              />
            ))}
      </>
    );
  }

  return (
    <div>
      <CustomModal
        isOpen={showParentCategoryModal}
        title={modalTitle || "Parent Category"}
        subtitle={modalSubTitle || "Select a parent category"}
        onClose={() => setShowParentCategoryModal(false)}
        onSubmit={handleCategoryClick}
        showButton={showButton}
        showCancelButton={true}
        showSubmitButton={true}
        isCentered={true}
        submitButtonText="Done"
        modalSize="modal-lg"
      >
        <div className="mb-3" style={{ position: 'relative' }}>
          <div 
            style={{
              position: 'absolute',
              left: '16px',
              top: '50%',
              transform: 'translateY(-50%)',
              zIndex: 2,
              color: isDarkMode ? '#ffffff' : '#6c757d',
              fontSize: '16px'
            }}
          >
            <i className="fa fa-search"></i>
          </div>
          <input
            type="text"
            className="form-control"
            placeholder="Search categories"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            style={{
              paddingLeft: searchTerm ? '48px' : '48px',
              paddingRight: searchTerm ? '48px' : '16px',
              borderRadius: '12px',
              border: isDarkMode ? '2px solid #555' : '2px solid #e9ecef',
              background: isDarkMode ? '#2d3436' : '#ffffff',
              color: isDarkMode ? '#ffffff' : '#000000',
              fontSize: '15px'
            }}
          />
          {searchTerm && (
            <span
              style={{
                position: 'absolute',
                right: '16px',
                top: '50%',
                transform: 'translateY(-50%)',
                cursor: 'pointer',
                color: isDarkMode ? '#ffffff' : '#6c757d',
                zIndex: 2,
                padding: '4px 8px',
                borderRadius: '4px',
                transition: 'all 0.2s ease'
              }}
              onClick={() => setSearchTerm("")}
              onMouseEnter={(e) => {
                e.currentTarget.style.background = isDarkMode ? '#555' : '#f8f9fa';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.background = 'transparent';
              }}
            >
              <i className="fa fa-times"></i>
            </span>
          )}
        </div>
        <div style={{ maxHeight: '400px', overflowY: 'auto', paddingRight: '8px' }}>
          {isLoadingCategories ? (
            <div className="d-flex flex-column align-items-center justify-content-center py-5">
              <div className="spinner-border text-primary" role="status" style={{ width: '3rem', height: '3rem' }}>
                <span className="visually-hidden">Loading...</span>
              </div>
              <p className="mt-3" style={{ color: isDarkMode ? '#ffffff' : '#6c757d' }}>Loading categories...</p>
            </div>
          ) : filteredCategories.length > 0 ? (
            <div>
              {filteredCategories
                .filter(category => category !== null && category !== undefined)
                .map((category, index) => (
                  <RenderCategoryRow
                    key={category.rootCat_ID}
                    category={category}
                    index={index}
                  />
                ))}
            </div>
          ) : (
            <div className="text-center py-5">
              <div 
                style={{
                  width: '60px',
                  height: '60px',
                  background: isDarkMode ? '#555' : '#f8f9fa',
                  borderRadius: '12px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  margin: '0 auto 16px',
                  border: isDarkMode ? '1px solid #666' : '1px solid #e9ecef'
                }}
              >
                <i className="fa fa-folder-open" style={{ fontSize: '24px', color: isDarkMode ? '#ffffff' : '#6c757d' }}></i>
              </div>
              <p style={{ color: isDarkMode ? '#ffffff' : '#6c757d', fontSize: '15px', margin: 0 }}>
                No categories found
                {searchTerm.length > 0
                  ? ` matching "${searchTerm}"`
                  : ""}
              </p>
            </div>
          )}
        </div>
      </CustomModal>
    </div>
  );
}